import useQuery from "../../../../../hooks/use-query";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import React, {useCallback, useEffect, useMemo} from "react";
import {Route, Switch, useLocation} from "react-router-dom";
import {Box, Typography} from "@mui/material";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import LostSalesAnalyticsDashboardView from "./dashboard";
import LostSearchAnalyticsLogsView from "./logs";
import {matchPath} from "react-router";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const tabs = {
    dashboard: 'dashboard',
    logs: 'logs',
}

const tabList = [
    {
        value: tabs.dashboard,
        label: "Dashboard",
    },
    {
        value: tabs.logs,
        label: "Logs",
    }
]

const LostSalesAnalyticsView = () => {
    const location = useLocation();
    const [, setQuery] = useQuery();
    const {configureNotebookTags} = useConfigureNotebook('lost-sales-analytics-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const tab = useMemo(() => {
        if (matchPath(location.pathname, {path: routes.analytics.lostSales.dashboard, exact: true})) {
            return tabs.dashboard;
        }
        if (matchPath(location.pathname, {path: routes.analytics.lostSales.logs, exact: true})) {
            return tabs.logs;
        }
        return false;
    }, [location.pathname])

    /**
     * Sets the industry id in the query object
     */
    useEffect(() => {
        if (!tab) {
            return setQuery(prevState => prevState, {location: {pathname: routes.analytics.lostSales.dashboard}, replace: true});
        }
    }, [setQuery, tab])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.lostSalesAnalytics);
        configureBehavioralTool((tags) => tags.lostSalesAnalytics);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Changes the tab in the query object
     * @type {(function(): void)}
     */
    const onTabChanged = useCallback((e, tab) => {
        switch (tab) {
            case tabs.dashboard:
                setQuery(prevState => ({
                    startDate: prevState.startDate,
                    endDate: prevState.endDate,
                }), {location: {pathname: routes.analytics.lostSales.dashboard}});
                break;
            case tabs.logs:
                setQuery(prevState => prevState, {location: {pathname: routes.analytics.lostSales.logs}});
                break;
            default:
                break;
        }
    }, [setQuery])

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBlockEnd: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Lost Sales Analytics
                </Typography>
            </Box>
            <div className={'w-100 my-3'}>
                <Tabs value={tab} onChange={onTabChanged}>
                    {
                        tabList?.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={tab.label}
                            />
                        ))
                    }
                </Tabs>
            </div>
            <Switch>
                <Route path={routes.analytics.lostSales.dashboard} exact>
                    <LostSalesAnalyticsDashboardView/>
                </Route>
                <Route path={routes.analytics.lostSales.logs} exact>
                    <LostSearchAnalyticsLogsView/>
                </Route>
            </Switch>
        </>
    );
}

export default LostSalesAnalyticsView;